import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { CcfBox, CcfTypography } from '@nice-ccf/ui-controls';
import { useEffect, useRef, useState } from 'react';
import styles from './simulator-v2-help.module.scss';
import { simulatorV2HelpSections } from './simulator-v2-help-content';

/* eslint-disable-next-line */
export interface SimulatorV2HelpPanelProps {
  open: boolean;
  onClose: () => void;
  /** selectedAction / active tab label used to auto-expand the matching section */
  activeSectionId?: string;
}

/**
 * Slide-out drawer showing the Simulator V2 user guide. The section whose
 * matchKeys include activeSectionId opens expanded and scrolled into view;
 * every other section stays collapsed but browsable via the table of contents.
 * @param props - SimulatorV2HelpPanelProps
 * @example 
 */
export function SimulatorV2HelpPanel(props: SimulatorV2HelpPanelProps) {
  const { open, onClose, activeSectionId } = props;
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isTocOpen, setIsTocOpen] = useState(false);

  const matchedSection = activeSectionId
    ? simulatorV2HelpSections.find((section) =>
        section.matchKeys.includes(activeSectionId)
      )
    : undefined;


  /**
   * Scrolls the given section into view.
   * @example scrollToSection('copilot')
   */
  const scrollToSection = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  /**
   * Collapses the table of contents, then scrolls to the chosen section so
   * its content is immediately visible instead of sitting below the full list.
   * @example handleTocSelect('copilot')
   */
  const handleTocSelect = (id: string) => {
    setIsTocOpen(false);
    setTimeout(() => scrollToSection(id), 150);
  };

  // Scroll the matched section into view once it has rendered expanded.
  useEffect(() => {
    if (!open || !matchedSection) return undefined;
    const timer = setTimeout(() => scrollToSection(matchedSection.id), 150);
    return () => clearTimeout(timer);
  }, [open, matchedSection]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <CcfBox className={styles.panelRoot}>
        <CcfBox className={styles.panelHeader}>
          <CcfTypography className={styles.panelTitle}>
            Simulator User Manual
          </CcfTypography>
          <IconButton onClick={onClose} aria-label="Close help">
            <CloseIcon />
          </IconButton>
        </CcfBox>

        <Accordion
          className={styles.tocContainer}
          expanded={isTocOpen}
          onChange={(_e, expanded) => setIsTocOpen(expanded)}
          disableGutters
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <CcfTypography className={styles.tocLabel}>
              Jump to a section
            </CcfTypography>
          </AccordionSummary>
          <AccordionDetails className={styles.tocDetails}>
            <List dense disablePadding>
              {simulatorV2HelpSections.map((section) => (
                <ListItemButton
                  key={section.id}
                  onClick={() => handleTocSelect(section.id)}
                  className={styles.tocItem}
                >
                  <ListItemText primary={section.title} />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <CcfBox className={styles.sectionsContainer} key={activeSectionId}>
          {simulatorV2HelpSections.map((section) => (
            <div
              key={section.id}
              ref={(el) => (sectionRefs.current[section.id] = el)}
            >
              <Accordion
                defaultExpanded={matchedSection?.id === section.id}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <CcfTypography className={styles.sectionTitle}>
                    {section.title}
                  </CcfTypography>
                </AccordionSummary>
                <AccordionDetails>{section.body}</AccordionDetails>
              </Accordion>
            </div>
          ))}
        </CcfBox>
      </CcfBox>
    </Drawer>
  );
}

export default SimulatorV2HelpPanel;
